import { Partner, PartnerResource, ScopedMeetingRef } from "./partners";

/**
 * Who, if anyone, already owns a continuous room key privately.
 *
 * A `continuousRoomKey` groups meetings so they share ONE chat/session. The
 * meetings themselves stay independent — a morning Power Hour and an evening
 * Community Session can share a key — but because the chat is shared, a key
 * must never mix a partner's private meeting with a public one, or with a
 * different partner's private meeting: whoever joins through the public
 * meeting lands in the private community's chat.
 *
 * That invariant is cheap to keep at authoring time and impossible to recover
 * from afterwards, so it is enforced here, when the key is saved, rather than
 * with scope inheritance in the backend read paths.
 */
export interface KeyOwner {
  partnerId: string;
  partnerName: string;
}

/** How a key is currently used by meetings OTHER than the one being saved. */
export interface KeyUsage {
  /** The partner that owns the key privately, if any. */
  owner: KeyOwner | null;
  /** True when at least one PUBLIC meeting already uses the key. */
  usedByPublic: boolean;
}

/** One meeting as the caller knows it: its key and its calendar identity. */
export interface KeyedMeeting {
  gcalEventId?: string;
  recurringMeetingId?: string;
  continuousRoomKey?: string;
}

const refMatches = (ref: ScopedMeetingRef, meeting: KeyedMeeting): boolean =>
  (!!ref.gcalEventId && ref.gcalEventId === meeting.gcalEventId) ||
  (!!ref.recurringMeetingId &&
    ref.recurringMeetingId === meeting.recurringMeetingId);

/**
 * The partner that privately owns `key`, or null when the key is free.
 *
 * `candidates` is every meeting the caller can see with its key — the calendar
 * list already holds this, so no extra per-meeting fetch is needed. Only the
 * per-partner scoped-meeting lists are fetched, and those are cached.
 *
 * `self` is excluded from the check so re-saving a meeting never conflicts with
 * itself.
 */
export async function findKeyUsage(
  key: string,
  candidates: KeyedMeeting[],
  self: KeyedMeeting,
  partners?: Partner[],
): Promise<KeyUsage> {
  const free: KeyUsage = { owner: null, usedByPublic: false };
  const trimmed = key.trim();
  if (!trimmed) return free;

  // Re-saving a meeting must never conflict with itself, and an occurrence
  // must not conflict with its own series either.
  const isSelf = (m: KeyedMeeting) =>
    (!!self.gcalEventId && m.gcalEventId === self.gcalEventId) ||
    (!!self.recurringMeetingId &&
      m.recurringMeetingId === self.recurringMeetingId);

  const sharing = candidates.filter(
    (m) => m.continuousRoomKey?.trim() === trimmed && !isSelf(m),
  );
  if (sharing.length === 0) return free;

  const list = partners ?? (await PartnerResource.getListCached());
  const scopedByPartner = await Promise.all(
    list.map(async (partner) => ({
      partner,
      scoped: await PartnerResource.getScopedMeetingsCached(partner.id).catch(
        () => [] as ScopedMeetingRef[],
      ),
    })),
  );

  let owner: KeyOwner | null = null;
  let usedByPublic = false;
  for (const m of sharing) {
    const holder = scopedByPartner.find(({ scoped }) =>
      scoped.some((ref) => refMatches(ref, m)),
    );
    if (holder) {
      owner ??= {
        partnerId: holder.partner.id,
        partnerName: holder.partner.displayName,
      };
    } else {
      // Not scoped to any partner — a public meeting already holds this key.
      usedByPublic = true;
    }
  }
  return { owner, usedByPublic };
}

/**
 * The message to block a save with, or null when the combination is allowed.
 *
 * `desiredPartnerId` is the partner the meeting is being made private to, or
 * null when it is being saved public. Same partner as the key's current owner
 * is fine — that is the normal case of grouping a community's own sessions.
 */
export function roomKeyConflictMessage(
  usage: KeyUsage,
  desiredPartnerId: string | null,
): string | null {
  const { owner, usedByPublic } = usage;

  if (!desiredPartnerId) {
    // Saving PUBLIC: only a private owner is a problem.
    return owner
      ? `This key is used for a private meeting of partner ${owner.partnerName}. Use a different key.`
      : null;
  }

  // Saving PRIVATE: another partner's key is a conflict...
  if (owner && owner.partnerId !== desiredPartnerId) {
    return `This key is already used by a private room of another partner (${owner.partnerName}). Use a different key.`;
  }
  // ...and so is a key a public meeting already holds, reached from the other
  // direction: the public meeting's joiners would land in this community's
  // shared chat.
  if (usedByPublic) {
    return "This key is already used by a public meeting. Use a different key, or make that meeting private to the same partner.";
  }
  return null;
}
